/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { IbanElement } from '@stripe/react-stripe-js';
import {
    StripeElementLocale,
    StripeIbanElementChangeEvent } from "@stripe/stripe-js";
import UIConstants from "../../../UIConstants";
import { getCountryGermany } from "../../Internationalisierung/Country";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import { WithTooltipProps } from "../HOC/withTooltip";
import { StripeInput } from "../StripeCardElement";
import TextInput from "../TextInput";
import "./StripeSepaElement.scss";

export interface StripeSepaElementProps extends WithTooltipProps {
    onChange: (isComplete: boolean, fullName: string) => void;
    fullName: string;
    required?: boolean;
    hidden?: boolean;
}

export default function StripeSepaElement(props: StripeSepaElementProps) {
    const { t } = useTranslation();
    const [error, setError] = useState<string>("");
    const isComplete = useRef<boolean>(false);

    return (
        <Box
            sx={{
                minWidth: "350px",
                ...(props.hidden && { display: "none" })
            }}
        >
            <TextInput
                caption={t(translationKeys.stripeSepaElement.vollstaendigerName)}
                feldBezeichnung=""
                typBezeichnung=""
                placeholder={t(
                    translationKeys.stripeSepaElement.vollstaendigerNamePlaceholder
                )}
                onChange={(e) => props.onChange(isComplete.current, e.target.value)}
                value={props.fullName}
                required={props.required}
            />
            <TextInput
                caption={t(translationKeys.stripeSepaElement.iban)}
                feldBezeichnung=""
                typBezeichnung=""
                errors={error ? [error]: []}
                InputProps={{
                    inputComponent: StripeInput as any,
                    inputProps: {
                        component: IbanElement,
                        stripeOnChange: (e: StripeIbanElementChangeEvent) => {
                            setError(e.error?.message ?? "");
                            isComplete.current = e.complete;
                            props.onChange(e.complete, props.fullName)
                        },
                        options: {
                            supportedCountries: ['SEPA'],
                            placeholderCountry: UIConstants.kulturShortLower as StripeElementLocale ?? getCountryGermany().languageCode,
                            classes: {
                                base: "lan-stripecardelement__iban"
                            },
                            style: {
                                base: { fontSize: "16px" },
                                empty: { "::placeholder": { color: "#b0b0b0" } },
                            }
                        }
                    },
                }}
                required={props.required}
            />
            <Typography sx={{ fontStyle: "italic", fontSize: "85%" }} color="textSecondary">
                {t(translationKeys.stripeSepaElement.mandat)}
            </Typography>
        </Box>
    );
}
